import omit from 'lodash.omit';

import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../constants';
import {
  getAccountCardElementsConfiguration,
  isCardElementEditable,
} from '../../components/AccountCard/helpers';
import { isElementCardBackground } from '../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';

/**
 * Returns CardElementConfigurationInput by element type.
 *
 * @param {Object} element The element state.
 * @param {Object} config The configuration.
 * @returns {{sticker_element_configuration: *}|{}|{photo_element_configuration: *}|{rectangle_element_configuration: *}|{text_element_configuration: *}}
 */
export const getCardElementConfigurationInputByElement = (element, config) => {
  const { cardElement, cardElementConfiguration } = element;
  const existingConfig = {
    label: cardElementConfiguration?.label,
    help_placeholder: cardElementConfiguration?.help_placeholder,
    border: cardElementConfiguration?.border
      ? {
          type: cardElementConfiguration.border.type,
          width: cardElementConfiguration.border.width,
          color: cardElementConfiguration.border.color,
          opacity: cardElementConfiguration.border.opacity,
        }
      : undefined,
    corner_radius: element.cardElementConfiguration?.corner_radius,
  };

  switch (cardElement.type) {
    case CARD_ELEMENT_TYPE.TEXT:
      return {
        ...existingConfig,
        is_editable: config?.is_editable,
        text_element_configuration: {
          font_family: cardElementConfiguration?.font_family,
          font_color: cardElementConfiguration?.font_color,
          text_align: cardElementConfiguration?.text_align,
          font_size: cardElementConfiguration?.font_size,
          font_weight: cardElementConfiguration?.font_weight,
          text_transform: cardElementConfiguration?.text_transform,
          text: cardElementConfiguration?.text,
          ...omit(config, ['is_editable']),
        },
      };
    case CARD_ELEMENT_TYPE.PHOTO:
      return {
        ...existingConfig,
        is_editable: config?.is_editable,
        photo_element_configuration: {
          ...omit(config, ['is_editable', 'orientation']),
        },
      };
    case CARD_ELEMENT_TYPE.STICKER:
      return {
        ...existingConfig,
        sticker_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.RECTANGLE:
      return {
        ...existingConfig,
        rectangle_element_configuration: config,
      };
    case CARD_ELEMENT_TYPE.ADDRESS:
      return {
        ...existingConfig,
        address_element_configuration: config,
      };
    default:
      return {};
  }
};

/**
 * Determines whether a CardElementPhotoConfiguration is complete.
 *
 * @param {Object} cardElementConfiguration The CardElementPhotoConfiguration.
 * @returns {boolean}
 */
export const isCardElementPhotoConfigurationComplete =
  cardElementConfiguration => {
    if (!cardElementConfiguration) {
      // Return true here if there's nothing to configure.
      return true;
    }

    const { photo, asset_image: assetImage } = cardElementConfiguration;

    return !!photo || !!assetImage;
  };

/**
 * Determines whether a CardElementAddressConfiguration is complete.
 *
 * @param {Object} cardElementConfiguration The CardElementAddressConfiguration.
 * @returns {boolean}
 */
export const isCardElementAddressConfigurationComplete =
  cardElementConfiguration => {
    if (!cardElementConfiguration) {
      return false;
    }

    return (
      !!cardElementConfiguration.external_shipping_address ||
      cardElementConfiguration.external_shipping_addresses?.records?.length >
        0 ||
      cardElementConfiguration.roster_identity_linked_identity_addresses
        ?.records?.length > 0
    );
  };

/**
 * Determines whether a CardElementTextConfiguration is complete.
 *
 * @param {Object} cardElementConfiguration The CardElementTextConfiguration.
 * @returns {boolean}
 */
export const isCardElementTextConfigurationComplete =
  cardElementConfiguration => {
    if (!cardElementConfiguration) {
      return false;
    }

    return cardElementConfiguration?.text?.length > 0;
  };

/**
 * Catch-all method to check whether a CardElement's configuration
 * is complete. Requires the CardElementConfiguriation object type
 * complete with its .card_element.type resolver.
 *
 * @param {Object} cardElementConfiguration The CardElementConfiguration.
 * @param {Object} cardElementConfiguration.card_element The CardElement.
 * @param {CARD_ELEMENT_TYPE} cardElementConfiguration.card_element.type The CardElementType.
 * @returns {boolean}
 */
export const isCardElementConfigurationComplete = cardElementConfiguration => {
  if (!cardElementConfiguration || !cardElementConfiguration?.card_element) {
    return false;
  }

  const { card_element: cardElement } = cardElementConfiguration;

  switch (cardElement.type) {
    case CARD_ELEMENT_TYPE.PHOTO:
      return isCardElementPhotoConfigurationComplete(cardElementConfiguration);
    case CARD_ELEMENT_TYPE.ADDRESS:
      return isCardElementAddressConfigurationComplete(
        cardElementConfiguration,
      );
    case CARD_ELEMENT_TYPE.TEXT:
      return isCardElementTextConfigurationComplete(cardElementConfiguration);
    default:
      return true;
  }
};

/**
 * Checks whether an AccountCard has enough configuration
 * to be checked out.
 *
 * TODO [cards] replace with GQL implementation.
 *
 * @param {Object} accountCard The AccountCard.
 * @returns {boolean}
 */
export const accountCardConfigurationValidate = accountCard => {
  if (!accountCard) {
    return false;
  }

  const elements = getAccountCardElementsConfiguration(accountCard);
  const elementPhotos = elements.filter(element => {
    const { cardElement, side } = element;
    const isEditable = isCardElementEditable(element);

    return (
      side === CARD_SIDE.FRONT &&
      cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
      isEditable
    );
  });

  const elementAddress = elements.find(element => {
    return element.cardElement.type === CARD_ELEMENT_TYPE.ADDRESS;
  });

  const elementText = elements.find(element => {
    return (
      element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
      element.side === CARD_SIDE.BACK
    );
  });

  // At least the front facing user editable photos are complete (if they exist)
  if (
    elementPhotos?.length > 0 &&
    elementPhotos.some(
      elementPhoto =>
        !isCardElementPhotoConfigurationComplete(
          elementPhoto.cardElementConfiguration,
        ),
    )
  ) {
    return false;
  }

  // Address must be filled
  if (
    elementAddress &&
    !isCardElementAddressConfigurationComplete(
      elementAddress.cardElementConfiguration,
    )
  ) {
    return false;
  }

  if (
    elementText &&
    !isCardElementTextConfigurationComplete(
      elementText.cardElementConfiguration,
    )
  ) {
    return false;
  }

  return true;
};

/**
 * Maps AccountCard data for checkout.
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard The AccountCard.
 * @param {Object} options.globalAccountCard The global AccountCard CustomPrint.
 * @param {string} [options.promoCode] The promo code.
 * @returns {undefined|{custom_print_items: [{quantity: number, configuration: {account_card: {account_card_id: *}, photos: {[p: string]: *, position: *}[]}, album_custom_print_id: *}]}}
 */
export const mapAccountCardToCheckoutInput = ({
  accountCard,
  globalAccountCard,
  promoCode,
}) => {
  if (!accountCard || !globalAccountCard) {
    return undefined;
  }

  return {
    custom_print_items: [
      {
        album_custom_print_id: globalAccountCard?.id,
        quantity: 1,
        configuration: {
          photos: [],
          account_card: {
            account_card_id: accountCard?.id,
          },
        },
      },
    ],
    promo_code: promoCode,
  };
};

/**
 * Returns the default CardSides of a card (the first CardSide for each
 * side of each firs CardSideGroup).
 *
 * @param {Object} [card] The Card.
 * @returns {{back_card_side_id: *, front_card_side_id: *}}
 */
export const getDefaultCardSideIds = card => {
  if (!card) {
    return {
      front_card_side_id: undefined,
      back_card_side_id: undefined,
    };
  }

  const cardSideGroupFront = card.card_side_groups.records.find(
    x => x.side === CARD_SIDE.FRONT,
  );
  const cardSideGroupBack = card.card_side_groups.records.find(
    x => x.side === CARD_SIDE.BACK,
  );

  const [cardSideFront] = cardSideGroupFront?.card_sides?.records ?? [];
  const [cardSideBack] = cardSideGroupBack?.card_sides?.records ?? [];

  return {
    front_card_side_id: cardSideFront?.id,
    back_card_side_id: cardSideBack?.id,
  };
};

/**
 * Returns whether an AccountCard allows to change the back design
 * by checking if it has multiple CardSideGroups on the back side.
 *
 * @param {Object} [accountCard] The AccountCard.
 * @returns {boolean}
 */
export const canSelectBackCardSideGroups = accountCard => {
  if (!accountCard) {
    return false;
  }

  const backCardSideGroups = accountCard.card.card_side_groups.records.filter(
    cardSideGroup => cardSideGroup.side === CARD_SIDE.BACK,
  );

  return backCardSideGroups?.length > 1;
};

/**
 * Given a cardElementConfigurationHistory array it returns unique configuration history for PHOTO CardElements.
 *
 * @param {Array} cardElementConfigurationHistory The CardElementConfiguration history.
 * @returns {*[]|*}
 */
export const getUniqueCardElementPhotoConfiguration =
  cardElementConfigurationHistory => {
    if (!cardElementConfigurationHistory?.length) {
      return [];
    }

    const cardElementConfigurationPhotos = cardElementConfigurationHistory
      // Filter out all CardElements except for PHOTO elements.
      .filter(cardElementConfiguration => {
        return (
          cardElementConfiguration.card_element.type === CARD_ELEMENT_TYPE.PHOTO
        );
      })
      // Unique by CardElement and only the most recent configuration per CardElement.
      .reduceRight((acc, cardElementConfiguration) => {
        if (
          acc.find(
            x => x.card_element.id === cardElementConfiguration.card_element.id,
          )
        ) {
          return acc;
        }

        return [...acc, cardElementConfiguration];
      }, [])
      // Restore correct order.
      .reverse()
      // Map data in a more usable format.
      .map(cardElementConfiguration => {
        return {
          card_element_id: cardElementConfiguration.card_element.id,
          photo_id: cardElementConfiguration.photo?.id,
          asset_image_id: cardElementConfiguration.asset_image?.id,
        };
      });

    return cardElementConfigurationPhotos;
  };

export const getBackgroundAccountCardElement = ({ accountCard }) => {
  if (!accountCard) {
    return undefined;
  }

  const accountCardSide = accountCard.account_card_sides?.[0];

  const photoCardElements = (
    accountCardSide?.card_side?.card_template?.card_elements ?? []
  ).filter(element => element.type === CARD_ELEMENT_TYPE.PHOTO);

  const backgroundCardElement = photoCardElements.find(cardElement => {
    const cardElementConfiguration =
      accountCardSide.card_element_configurations.find(
        config => config.card_element.id === cardElement.id,
      );

    return isElementCardBackground({ cardElement, cardElementConfiguration });
  });

  if (backgroundCardElement) {
    return {
      cardElement: backgroundCardElement,
      cardElementConfiguration:
        accountCardSide.card_element_configurations.find(
          config => config.card_element.id === backgroundCardElement.id,
        ),
    };
  }

  return undefined;
};
export const getMatchingTextElements = ({ element, accountCard }) => {
  if (!element) {
    return [];
  }

  if (!accountCard) {
    return [element];
  }

  const { cardElement, cardElementConfiguration } = element;

  const { frame } = cardElement;
  const {
    card_side: cardSide,
    card_element_configurations: cardSideCardElementConfigurations,
    side,
  } = accountCard?.account_card_sides?.[0] ?? {};
  const cardSideCardElementConfiguration =
    cardSide?.card_element_configurations?.find(
      config => config.card_element.id === cardElement.id,
    );
  const { help_placeholder: placeHolder } =
    cardSideCardElementConfiguration ?? cardElementConfiguration ?? {};

  const matchingElements = cardSide?.card_template?.card_elements
    .filter(possibleCardElement => {
      // No need to add the same element twice
      if (possibleCardElement.id === cardElement.id) {
        return false;
      }

      // Avoid any cardElement of a different type
      if (possibleCardElement.type !== cardElement.type) {
        return false;
      }

      // Avoid any cardElement with a different frame sizing
      if (
        possibleCardElement.frame.width !== frame.width ||
        possibleCardElement.frame.height !== frame.height
      ) {
        return false;
      }

      const possibleCardElementConfiguration = (
        cardSide?.card_element_configurations ?? []
      )?.find(config => config.card_element.id === possibleCardElement.id);

      // Avoid any cardElement with a different helper placeholder
      if (possibleCardElementConfiguration?.help_placeholder !== placeHolder) {
        return false;
      }

      return true;
    })
    .map(matchingCardElement => ({
      side,
      cardElement: matchingCardElement,
      cardElementConfiguration:
        (cardSideCardElementConfigurations ?? [])?.find(
          config => config.card_element.id === matchingCardElement.id,
        ) ??
        (cardSide?.card_element_configurations ?? [])?.find(
          config => config.card_element.id === matchingCardElement.id,
        ),
    }));

  return [element, ...matchingElements];
};

export const getMatchingImageElements = ({ element, accountCard }) => {
  if (!element) {
    return [];
  }

  if (!accountCard) {
    return [element];
  }

  const { cardElement } = element;

  const { frame } = cardElement;
  const {
    card_side: cardSide,
    card_element_configurations: cardSideCardElementConfigurations,
    side,
  } = accountCard?.account_card_sides?.[0] ?? {};

  const matchingElements = cardSide?.card_template?.card_elements
    .filter(possibleCardElement => {
      // No need to add the same element twice
      if (possibleCardElement.id === cardElement.id) {
        return false;
      }

      // Avoid any cardElement of a different type
      if (possibleCardElement.type !== cardElement.type) {
        return false;
      }

      // Avoid any cardElement with a different frame sizing
      if (
        possibleCardElement.frame.width !== frame.width ||
        possibleCardElement.frame.height !== frame.height
      ) {
        return false;
      }

      return true;
    })
    .map(matchingCardElement => ({
      side,
      cardElement: matchingCardElement,
      cardElementConfiguration:
        (cardSideCardElementConfigurations ?? [])?.find(
          config => config.card_element.id === matchingCardElement.id,
        ) ??
        (cardSide?.card_element_configurations ?? [])?.find(
          config => config.card_element.id === matchingCardElement.id,
        ),
    }));

  return [element, ...matchingElements];
};
